import {addLineItems, getCart} from '@/cart/cart-store';
import ButtonLoadingIndicator from '@/components/ButtonLoadingIndicator';
import LoadingIndicator from '@/components/LoadingIndicator';
import {useNavigate} from '@/hooks/use-navigate';
import {getDefaultOnetimePlan} from '@/product/product-selection';
import {PostAtcUpsellDialogMetaobject, VariantState} from '@/product/types';
import {getProductSellingPlans} from '@/selling-plan/selling-plan-store';
import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import formatCurrency from '@/utilities/format-currency';
import {handleize} from '@/utilities/handleize';
import {logError} from '@/utilities/log';
import {ChangeEvent} from 'preact/compat';
import {useCallback, useState} from 'preact/hooks';

export type UpsellDialogProps = PostAtcUpsellDialogMetaobject;

/**
 * Post ATC upsell dialog component.
 * This is a mini buy-stack that can be displayed in a modal after the customer adds to cart.
 * It can be configured using the postAtcUpsellDialog metaobject.
 *
 * @param acceptButtonLabel - The label for the accept button
 * @param body - The body text of the dialog
 * @param declineButtonLabel - The label for the decline button
 * @param product - The product to display in the dialog
 * @param subtitle - The subtitle of the dialog
 * @param title - The title of the dialog
 * @returns The post ATC upsell dialog component
 */
export default function UpsellDialog({
  acceptButtonLabel = 'ADD TO CART',
  body = 'Dishwasher Detergent Pods. Plant and Mineral Based Active Cleaning Ingredients with No Harsh Chemicals. All Natural Fragrance.',
  declineButtonLabel = 'NO, CONTINUE TO CART',
  product,
  subtitle = 'UPGRADE YOUR CART AND SAVE',
  title = 'GET THE MATCHING DISHWASHER DETERGENT PODS',
}: UpsellDialogProps) {
  const variantSelectLabel = product.options.map(({name}) => name).join(' / ');
  const [selectedVariant, setSelectedVariant] = useState<VariantState | null>(
    product.selectedOrFirstAvailableVariant
  );
  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false);
  const cart = getCart();
  const currency = cart?.currency ?? 'USD';
  const price = selectedVariant?.price ?? 0;
  const plans = getProductSellingPlans(product.id);
  const onetimePlan = getDefaultOnetimePlan(plans);
  const navigate = useNavigate();

  /**
   * Navigate to the cart page
   */
  const goToCart = useCallback(() => {
    navigate('/cart');
  }, [navigate]);

  /**
   * Handles the change event of the variant selector.
   * Updates the selected variant state.
   */
  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newVariant = product.variants.find(
      ({id}) => id === event.currentTarget?.value
    );
    if (!newVariant) return;

    setSelectedVariant(newVariant);
  };

  /**
   * Handles the click event for the add to cart button
   * Adds the selected variant to cart and navigates to the cart page.
   */
  const handleAddToCart = async () => {
    if (!selectedVariant) return;

    setIsLoadingAddToCart(true);

    try {
      await addLineItems([
        {
          id: parseInt(selectedVariant.id),
          quantity: 1,
          selling_plan: onetimePlan?.external_plan_id ?? undefined,
        },
      ]);
      goToCart();
    } catch (error) {
      logError(error);
    } finally {
      setIsLoadingAddToCart(false);
    }
  };

  return (
    <div className="flex min-h-full w-max max-w-full items-center lg:max-w-192">
      <div className="grid grid-cols-1 items-center gap-y-2 sm:gap-y-6">
        <div className="flex flex-col gap-y-1">
          <h6 className="text-center text-deep-ocean-blue sm:font-m-header-524135-left-21">
            {title}
          </h6>
          <span className="font-body-5-bold hidden text-center md:block">
            {subtitle}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:gap-y-6 md:grid-cols-[minmax(0,3fr)_minmax(0,4fr)]">
          <div className="relative h-[clamp(8rem,24dvh,16rem)] w-full md:h-0 md:min-h-full">
            <img
              src={createShopifyImageUrl(
                selectedVariant?.image?.url ?? product.featuredImage?.url ?? '',
                440
              )}
              alt={`${product.title} ${selectedVariant?.title}`}
              className="size-full object-contain object-top"
            />
          </div>
          <div className="flex flex-col gap-y-4 sm:gap-y-5">
            <div className="flex w-full flex-col gap-y-2 sm:gap-y-3">
              <p className="font-body-6 text-start text-deep-ocean-blue xs:font-body-5">
                {body}
              </p>
              <span className="text-start text-xl font-bold tracking-wide sm:text-2xl">
                <span className="text-color-primary-4">
                  {formatCurrency(price, currency)}
                </span>
              </span>
            </div>
            <label
              htmlFor={`${handleize(product.title)}-variants`}
              className="flex w-full flex-col gap-y-2"
            >
              <span className="font-body-5-bold text-start text-deep-ocean-blue">
                {variantSelectLabel}
              </span>
              <select
                name={`${product.title} Variants`}
                id={`${handleize(product.title)}-variants`}
                onChange={handleChange}
              >
                {product.variants.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex w-full flex-col gap-y-2">
              <ButtonLoadingIndicator
                fullWidth
                isLoading={isLoadingAddToCart}
                button={
                  <button
                    className="btn btn-square btn-purchase w-full text-lg font-semibold sm:text-xl"
                    onClick={() => void handleAddToCart()}
                  >
                    {acceptButtonLabel}
                  </button>
                }
                loader={<LoadingIndicator color="fresh-scent" />}
              />
              <button
                className="btn btn-sm btn-outline btn-square w-full border-none px-6 py-3 hover:bg-transparent"
                onClick={goToCart}
              >
                {declineButtonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
